import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { Button, InputHelperText, InputLabel, Modal, Tooltip, Typography } from 'ui-kit';
import { ERefundType } from './contants';
import { IOrder, IOrderProductInline, refundOrder } from 'api/order_sdk';
import { IOrderProduct, refundOrderProduct } from 'api/order_product_sdk';

import { formatCurrency } from 'utils';
import LoadingModal from 'components/LoadingModal';
import { Input } from 'components/Form';
import { message } from 'antd';

import { usePollingRefund } from './utils';

export interface IRefundButton {
  title: string;
  disabled: boolean;
  refundType: ERefundType;
  order?: IOrder;
  orderProduct?: IOrderProductInline | IOrderProduct; // if there is IOrderProductInline, it's neccessary to have order as well
  onRefundCompleted: () => void;
  nonRefundableReason?: string;
  onExpand?: (orderId: number) => void;
}

const RefundButton = ({
  title,
  disabled,
  refundType,
  order,
  onRefundCompleted,
  orderProduct,
  nonRefundableReason,
  onExpand,
}: IRefundButton) => {
  const [isRefunding, setIsRefunding] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [itemIdForRefund, setItemIdForRefund] = useState<number | null>(null);

  const [quantityToRefund, setQuantityToRefund] = useState<number | null>(
    orderProduct?.quantity_to_refund,
  );

  const isQuantityValid = useMemo(() => {
    if (refundType === ERefundType.ORDER_PRODUCT) {
      if (quantityToRefund > 0 && quantityToRefund <= orderProduct?.quantity_to_refund) {
        return true;
      }
      return false;
    }
    return true;
  }, [quantityToRefund, orderProduct, refundType]);

  const productNames = useMemo(() => {
    if (refundType === ERefundType.ORDER_PRODUCT && !_.isUndefined(orderProduct)) {
      return [orderProduct.product_name];
    }
    return order.purchases.map((purchase) => purchase.order_products.map((op) => op.product_name));
  }, [order, orderProduct]);

  const refundAmount = useMemo(
    () =>
      refundType === ERefundType.ORDER_PRODUCT && !_.isUndefined(orderProduct)
        ? (quantityToRefund / orderProduct.quantity) * orderProduct.amount_to_refund
        : order.grand_total,
    [order, orderProduct, quantityToRefund],
  );

  const isPartialRefundAvailable = useMemo(() => {
    if (refundType === ERefundType.ORDER && order) {
      const hasSplittableProduct = order.purchases?.some((purchase) =>
        purchase.order_products?.some((op) => (op.quantity_to_refund || 0) > 1),
      );

      return hasSplittableProduct || false;
    }

    if (refundType === ERefundType.ORDER_PRODUCT && orderProduct) {
      return (orderProduct.quantity_to_refund || 0) > 1;
    }

    return false;
  }, [refundType, order, orderProduct]);

  useEffect(() => {
    setQuantityToRefund(orderProduct?.quantity_to_refund);
  }, [orderProduct?.quantity_to_refund]);

  const handleRefund = useCallback(() => {
    setIsRefunding(true);

    if (refundType === ERefundType.ORDER) {
      refundOrder({ orderId: order.id })
        .then(() => {
          setIsOpenConfirmModal(false);
          setItemIdForRefund(order.id);
        })
        .catch((error) => message.error(error.message));
    } else if (refundType === ERefundType.ORDER_PRODUCT && !_.isUndefined(orderProduct)) {
      refundOrderProduct({
        orderProductId: orderProduct.id,
        quantity: quantityToRefund,
      })
        .then(() => {
          setIsOpenConfirmModal(false);
          setItemIdForRefund(orderProduct.id);
        })
        .catch((error) => message.error(error.message));
    }
  }, [refundType, order, orderProduct, quantityToRefund]);

  const { isTaskReady } = usePollingRefund(refundType, itemIdForRefund);

  useEffect(() => {
    if (isTaskReady) {
      onRefundCompleted();
      setIsRefunding(false);
      message.info('Your refund has been processed successfully.');
    }
  }, [isTaskReady, onRefundCompleted]);

  if (isRefunding) {
    return (
      <LoadingModal
        title="Refunding"
        topCopy="Your refund is being processed."
        bottomCopy="Please don’t leave or refresh this page until the process is done."
        isVisible={isRefunding}
      />
    );
  }

  return (
    <>
      <Tooltip title={nonRefundableReason}>
        <span>
          {/*  The button should be wrap with span tag to show the tooltip when the button is disabled */}
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setIsOpenConfirmModal(true)}
            disabled={disabled}
          >
            {title}
          </Button>
        </span>
      </Tooltip>
      <Modal
        visible={isOpenConfirmModal}
        okText={title}
        title="Confirm refund"
        onOk={handleRefund}
        onCancel={() => {
          setIsOpenConfirmModal(false);
        }}
        okButtonProps={{
          disabled: !isQuantityValid,
        }}
        footer={[
          <Button key="back" onClick={() => setIsOpenConfirmModal(false)}>
            Cancel
          </Button>,
          refundType === ERefundType.ORDER && isPartialRefundAvailable && (
            <Button
              style={{ marginRight: '15px' }}
              variant="contained"
              onClick={() => {
                setIsOpenConfirmModal(false);
                onExpand(order.id);
              }}
            >
              Partial Refund
            </Button>
          ),
          <Button variant="contained" key="submit" onClick={handleRefund}>
            Refund
          </Button>,
        ]}
      >
        {refundType === ERefundType.ORDER_PRODUCT && !_.isUndefined(orderProduct) ? (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: 16,
              }}
            >
              <InputLabel label="Select quantity to refund" />

              <Input
                min={1}
                max={orderProduct.quantity}
                className={undefined}
                style={{ width: '80px' }}
                type="number"
                value={quantityToRefund}
                onChange={(e) => setQuantityToRefund(e.target.value)}
              />
            </div>

            {!isQuantityValid ? (
              <InputHelperText type="error">
                Quantity must be equal or greater to 1 and less or equal to{' '}
                {orderProduct.quantity_to_refund}
              </InputHelperText>
            ) : (
              <>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Are you sure you want to refund{' '}
                  <strong>
                    {order
                      ? order.guest.guest_name
                      : 'guest' in orderProduct
                      ? orderProduct.guest.guest_name
                      : 'N/A'}
                  </strong>{' '}
                  who purchased <strong>{orderProduct.product_name}</strong> for{' '}
                  <strong>{formatCurrency(refundAmount, orderProduct.currency)}</strong>?
                </Typography>
                <Typography variant="body2">This cannot be reversed.</Typography>{' '}
              </>
            )}
          </div>
        ) : (
          <div>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Are you sure you want to refund <strong>{order.guest.guest_name}</strong> for{' '}
              <strong>{productNames.join(', ')}</strong> with amount{' '}
              <strong>{formatCurrency(refundAmount, order.currency)}</strong>?
            </Typography>
            <Typography variant="body2">This cannot be reversed.</Typography>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RefundButton;
